import { Component, ErrorInfo, ReactNode } from "react";
import { captureException } from "@/lib/observability/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
  autoReloading: boolean;
}

// A stale browser tab (open since before the last deploy) still references the old
// hashed chunk filenames baked into the index.html it loaded — a route it hasn't
// visited yet then fails to lazy-load, since the old chunk file no longer exists on
// the server after a new publish overwrote the assets directory. Fetching a fresh
// index.html (a reload) resolves it immediately, so auto-recover instead of showing
// the scary error screen for what is, for the visitor, not a real error.
const STALE_CHUNK_ERROR_PATTERN =
  /fetch dynamically imported module|importing a module script failed|loading chunk .* failed/i;
const RELOAD_GUARD_KEY = "wnp-stale-chunk-reload-at";
// Guards against a genuine, persistent failure (e.g. offline) reload-looping forever —
// one auto-reload attempt per window is enough; a later failure outside the window
// (a subsequent deploy while the tab stayed open) gets its own attempt.
const RELOAD_GUARD_WINDOW_MS = 30_000;

function shouldAutoReload(error: Error): boolean {
  if (!STALE_CHUNK_ERROR_PATTERN.test(error.message)) return false;
  try {
    const lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Date.now() - lastAttempt > RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage unavailable (private mode, disabled storage) — allow the attempt,
    // worst case it retries every time rather than not at all.
    return true;
  }
}

function markReloadAttempted() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // best-effort only
  }
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, autoReloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log so it shows up in console / remote logs
    console.error("[ErrorBoundary] Uncaught error:", error, info);
    captureException(error, { componentStack: info.componentStack });

    if (shouldAutoReload(error)) {
      markReloadAttempted();
      this.setState({ info, autoReloading: true });
      window.location.reload();
      return;
    }

    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.autoReloading) {
      return (
        <main className="min-h-screen bg-background text-foreground flex items-center justify-center">
          <p className="text-sm text-muted-foreground">Obnovujeme stránku…</p>
        </main>
      );
    }
    if (this.state.error) {
      return (
        <main className="min-h-screen bg-background text-foreground p-4 flex items-start justify-center">
          <div className="max-w-xl w-full mt-12 space-y-4 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
            <h1 className="text-lg font-bold text-destructive">Nastala chyba pri načítavaní stránky</h1>
            <p className="text-sm text-muted-foreground">
              Skúste obnoviť stránku. Ak chyba pretrváva, pošlite tento výpis vývojárovi:
            </p>
            <pre className="text-xs bg-background border border-border rounded-md p-3 overflow-auto max-h-64 whitespace-pre-wrap break-words">
              {this.state.error.message}
              {this.state.error.stack ? "\n\n" + this.state.error.stack : ""}
              {this.state.info?.componentStack ? "\n\nKomponent:\n" + this.state.info.componentStack : ""}
            </pre>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90"
            >
              Obnoviť stránku
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
